import { TwinTowerCombineState, type TwinTowerCombineSnapshot } from './TwinTowerCombineState.js';
import type { TwinTowerCombineConfig, TwinTowerId } from './TwinTowerCombineConfig.js';

export type TwinTowerLayoutMode = 'standard' | 'compact';

export interface TwinTowerVisibleBounds {
  readonly left: number;
  readonly right: number;
  readonly top: number;
  readonly bottom: number;
}

export interface TwinTowerCombineCallbacks {
  readonly onStateChanged?: (snapshot: TwinTowerCombineSnapshot) => void;
  readonly onComplete?: (config: TwinTowerCombineConfig, snapshot: TwinTowerCombineSnapshot) => void;
}

/**
 * Rendering-independent mechanic controller. A future production-art view can
 * subscribe to state and layout changes without putting curriculum values in
 * Phaser display code.
 */
export class TwinTowerCombine {
  readonly state: TwinTowerCombineState;
  private completed = false;
  private layoutMode: TwinTowerLayoutMode = 'standard';

  constructor(
    readonly config: TwinTowerCombineConfig,
    private readonly callbacks: TwinTowerCombineCallbacks = {},
  ) {
    this.state = new TwinTowerCombineState(config);
  }

  transfer(from: TwinTowerId, amount = 1) {
    const result = this.state.transfer(from, amount);
    if (result === 'accepted' || result === 'complete') this.publish();
    return result;
  }

  reset() {
    this.completed = false;
    this.state.reset();
    this.publish();
  }

  layout(bounds: TwinTowerVisibleBounds) {
    const visibleWidth = bounds.right - bounds.left;
    const visibleHeight = bounds.bottom - bounds.top;
    this.layoutMode = visibleWidth < 1100 || visibleHeight < 640 ? 'compact' : 'standard';
    return this.layoutMode;
  }

  get currentLayoutMode() { return this.layoutMode; }
  get snapshot() { return this.state.snapshot; }

  private publish() {
    const snapshot = this.state.snapshot;
    this.callbacks.onStateChanged?.(snapshot);
    if (snapshot.isComplete && !this.completed) {
      this.completed = true;
      this.callbacks.onComplete?.(this.config, snapshot);
    }
  }
}
